<template>

  <SubHeading :home="home" :tabs="visibleTabs" :shorts="true" :currTab="currTab" color="cyan darken-3" @active="handleActive" />

  <!-- breadcrumbs   -->
  <nav class="sba-collection-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <span v-if="currTab?.title?.toLowerCase()?.includes('home')" class="breadcrumb sba-collection-crumb blue-grey-text">
          <span>HOD Home</span>
        </span>
        <a v-else href="!#" class="breadcrumb sba-collection-crumb blue-grey-text"
          @click.prevent="handleActive(tabs.find(x => x.title.includes('Home')))">
          <span>HOD Home</span>
        </a>
        <template v-if="!currTab?.title?.toLowerCase()?.includes('home')">
          <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
          <span class="breadcrumb sba-collection-crumb blue-grey-text">
            <span>{{ currTab?.title?.split(" ")[0] }}</span>
          </span>
        </template>
        <span class="sba-collection-context">{{ breadcrumbContext }}</span>
      </div>
    </div>
  </nav>

  <!-- search layer -->
  <!-- <div class="grey lighten-4">
    <div class="container section"></div>
  </div> -->

  <!-- Top -->
  <div id="top"></div>

  <!-- Start -->
  <div class="container hod-page-shell">
    <div class="hod-workbench">
      <LoadingDiv v-if="!isReady" />

      <template v-else>

        <div class="hod-view-shell sba-slide-in" :key="currTab?.id">
          <CollectionModuleLanding
            v-if="currTab?.title.toLowerCase().includes('home')"
            moduleName="HOD"
            heading="HOD Review Desk"
            description="Review high-level claim movement, resolve escalations, and keep department decisions consistent."
            scopeLabel="Department Control"
            accent="blue"
            :tabs="tabs"
            :cardCopy="landingCards"
            @active="handleActive"
          />

          <!-- Claims -->
          <ClaimsDiv
            :resetKey="claimsResetKey"
            v-if="currTab?.title.toLowerCase().includes('review')"
          />

          <!-- Report -->
          <ReportDiv v-if="currTab?.title.toLowerCase().includes('report')" />
        </div>

      </template>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import useModuleNavigation from "@/composables/useModuleNavigation";
import SubHeading from "@/components/HeaderSubDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import ClaimsDiv from "@/components/hod/HODClaimsComponent.vue";
import ReportDiv from "@/components/insight/ClaimsInsightComponent.vue";
import CollectionModuleLanding from "@/components/collection/CollectionModuleLandingComponent.vue";


export default {
  name: "HODHome",
  components: {
    SubHeading,
    LoadingDiv,
    ClaimsDiv,
    ReportDiv,
    CollectionModuleLanding,
  },
  setup() {
    const route = useRoute();
    const home = ref(null);
    const isReady = ref(true);
    const error = ref(null);
    const currTab = ref(null);
    const claimsResetKey = ref(0);
    const tabs = ref([]);
    const { activateLocalTab } = useModuleNavigation();
    const visibleTabs = computed(() => tabs.value.filter((tab) => !tab.title?.includes("Home")));
    const breadcrumbContext = computed(() => {
      if (currTab.value?.title?.toLowerCase()?.includes("home")) {
        return `${currTab.value?.name || "HOD"} Workspace`;
      }

      return `${currTab.value?.title || "HOD"} Workspace`;
    });
    const landingCards = {
      "Claims Review": {
        icon: "fact_check",
        description: "Review approved claim batches and pass final clinical decisions forward.",
      },
      "Report Download": {
        icon: "summarize",
        description: "Download operational reports for department-level oversight.",
      },
    };
    const routeSections = {
      "claims-review": "Claims Review",
      "report-download": "Report Download",
    };

    // start
    onMounted(() => {
      // page name
      document.title = "HOD | EasyHMO";
    });

    // menu
    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "HOD", title: "Home", icon: null, url: null, hash: null, });
      tabs.value.push({ id: 2, name: "HOD", title: "Claims Review", icon: null, url: null, hash: null, });
      tabs.value.push({ id: 3, name: "HOD", title: "Report Download", icon: null, url: null, hash: null, });

      // set default
      currTab.value = tabs.value.find(x => x.title.includes("Home"));
      // set home
      home.value = tabs.value.find(x => x.title.includes("Home"));

      activateRouteSection();
    });

    // handle curr status
    const handleActive = (tab) => {
      if (tab?.title?.toLowerCase()?.includes("claims")) {
        claimsResetKey.value++;
      }

      activateLocalTab(tab, currTab);
    };

    function activateRouteSection() {
      const targetTitle = routeSections[route.query.section];
      const targetTab = tabs.value.find(x => x.title === targetTitle);

      if (targetTab && currTab.value?.id !== targetTab.id) {
        handleActive(targetTab);
      }
    }

    watch(
      () => route.query.section,
      () => activateRouteSection()
    );


    return {
      home,
      handleActive,
      isReady,
      error,
      currTab,
      claimsResetKey,
      tabs,
      visibleTabs,
      breadcrumbContext,
      landingCards,
    };
  },
};
</script>

<style scoped>
.hod-page-shell {
  margin-top: -14px;
  padding-bottom: 44px;
  padding-top: 0;
  position: relative;
  z-index: 1;
}

.hod-workbench {
  color: #263238;
}

.hod-view-shell :deep(.collection-landing-head),
.hod-view-shell :deep(.workflow-card),
.hod-view-shell :deep(.premium-queue-head),
.hod-view-shell :deep(.claim-batch-row),
.hod-view-shell :deep(.claim-detail-card),
.hod-view-shell :deep(.empty-review-state),
.hod-view-shell :deep(.claims-report-head),
.hod-view-shell :deep(.report-form-panel),
.hod-view-shell :deep(.report-submit-strip) {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

@media (prefers-reduced-motion: reduce) {
  .hod-view-shell :deep(.collection-landing-head),
  .hod-view-shell :deep(.workflow-card),
  .hod-view-shell :deep(.premium-queue-head),
  .hod-view-shell :deep(.claim-batch-row),
  .hod-view-shell :deep(.claim-detail-card),
  .hod-view-shell :deep(.empty-review-state),
  .hod-view-shell :deep(.claims-report-head),
  .hod-view-shell :deep(.report-form-panel),
  .hod-view-shell :deep(.report-submit-strip) {
    animation: none;
  }
}

@media only screen and (max-width: 900px) {
  .hod-page-shell {
    width: 92%;
  }
}

@media only screen and (max-width: 600px) {
  .hod-page-shell {
    margin-top: -10px;
    padding-bottom: 34px;
    width: 94%;
  }

  .hod-view-shell :deep(.premium-queue-head),
  .hod-view-shell :deep(.claim-batch-row),
  .hod-view-shell :deep(.claim-detail-card),
  .hod-view-shell :deep(.empty-review-state) {
    border-radius: 6px;
  }
}
</style>
